/**
 * Componentes do cofre de segredos de sistema, reaproveitados pela Central de Integrações e pelas telas de outros
 * módulos (configuração fiscal, automações, atendimento): o segredo é gravado por RPC com 2FA e nunca volta à tela.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, PlugZap, Trash2, Upload, Wand2 } from "lucide-react";
import { from, rpc, unwrap, useAction, useHasRole, useOrg } from "@vixio/data";
import { Button, Callout, ConfirmDialog, StatusFromMap, TextInput, formatDateTime, formatRelative, useToast } from "@vixio/ui";
import { TEST_STATUS, type ConnectionTest, type SystemSecret, type TestProvider } from "./api";

/** Segredos da organização (catálogo + configurados), sem valores. Só administradores. */
export function useSystemSecrets() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  return useQuery({
    queryKey: ["integrations", "system_secrets", org.id],
    enabled: isAdmin,
    queryFn: () => rpc("integrations", "list_system_secrets", { p_org: org.id }),
  });
}

/** Segredo aleatório (32 bytes, base64url) gerado no navegador. */
export function randomSecret(bytes = 32): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  let s = "";
  for (const b of a) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function fileToBase64(f: File): Promise<string> {
  const buf = new Uint8Array(await f.arrayBuffer());
  let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

/** Linha "configurado em … por … · final ••••1234". */
export function SecretStatus({ row }: { row: SystemSecret | undefined }) {
  if (!row?.configured) return <span className="muted">Não configurado</span>;
  return (
    <span className="text-2" data-testid="secret-status">
      Configurado {row.updated_at ? <>em <span className="num">{formatDateTime(row.updated_at)}</span></> : null}
      {row.updated_by_email ? <> por {row.updated_by_email}</> : null}
      {row.last4 ? <> · final <span className="mono">••••{row.last4}</span></> : null}
    </span>
  );
}

export interface SecretFieldProps {
  /** Chave do cofre (ex.: smtp.password, fiscal.provider_token.<emissor>). */
  secretKey: string;
  label: string;
  hint?: string;
  /** password: digitar; file: enviar arquivo (vira Base64); generate: gerar no navegador e mostrar uma vez. */
  mode?: "password" | "file" | "generate";
  accept?: string;
  /** Chamado com a referência vault:<chave> depois de gravar. */
  onSaved?: (ref: string) => void;
  onCleared?: () => void;
}

/** Campo de segredo: grava no cofre (admin com 2FA), mostra só o estado, troca e remove. */
export function SecretField({ secretKey, label, hint, mode = "password", accept, onSaved, onCleared }: SecretFieldProps) {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const toast = useToast();
  const list = useSystemSecrets();
  const row = list.data?.find((r) => r.key === secretKey);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [shown, setShown] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setEditing(false);
    setValue("");
    setShown(null);
  }, [secretKey]);
  const save = useAction((v: string) => rpc("integrations", "set_system_secret", { p_org: org.id, p_key: secretKey, p_value: v }), {
    invalidate: [["integrations", "system_secrets"]],
    success: `${label}: guardado no cofre`,
    onSuccess: (r) => {
      setEditing(false);
      setValue("");
      onSaved?.(r.ref);
    },
  });
  const clear = useAction(() => rpc("integrations", "clear_system_secret", { p_org: org.id, p_key: secretKey }), {
    invalidate: [["integrations", "system_secrets"]],
    success: `${label}: removido do cofre`,
    onSuccess: () => onCleared?.(),
  });
  if (!isAdmin) return <div className="vx-field"><span className="vx-field__label">{label}</span><span className="muted">Só administradores veem e trocam segredos.</span></div>;
  const testId = `secret-${secretKey.replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="vx-field" data-testid={testId}>
      <span className="vx-field__label">{label}</span>
      <div className="vx-row" style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <SecretStatus row={row} />
        {!editing && mode !== "file" && mode !== "generate" ? (
          <Button size="sm" icon={<KeyRound />} onClick={() => setEditing(true)}>{row?.configured ? "Trocar" : "Definir"}</Button>
        ) : null}
        {mode === "generate" ? (
          <Button size="sm" icon={<Wand2 />} loading={save.isPending} onClick={() => { const v = randomSecret(); setShown(v); save.mutate(v); }}>
            {row?.configured ? "Gerar outro" : "Gerar segredo"}
          </Button>
        ) : null}
        {mode === "generate" && !editing ? <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Informar um valor</Button> : null}
        {mode === "file" ? (
          <>
            <input ref={fileRef} type="file" accept={accept} hidden onChange={async (e) => { const f = e.target.files?.[0]; if (f) save.mutate(await fileToBase64(f)); e.target.value = ""; }} />
            <Button size="sm" icon={<Upload />} loading={save.isPending} onClick={() => fileRef.current?.click()}>{row?.configured ? "Enviar outro arquivo" : "Enviar arquivo"}</Button>
          </>
        ) : null}
        {row?.configured ? <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setClearing(true)}>Remover</Button> : null}
      </div>
      {editing ? (
        // Sem <form>: o campo também aparece dentro de diálogos que já são formulários.
        <div className="vx-row" style={{ alignItems: "flex-end", marginTop: 6 }}>
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <TextInput label={`Novo valor: ${label}`} type="password" autoComplete="new-password" value={value} onChange={setValue} data-testid={`${testId}-input`}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (value.trim()) save.mutate(value); } }} />
          </div>
          <Button variant="primary" loading={save.isPending} disabled={!value.trim()} onClick={() => save.mutate(value)} data-testid={`${testId}-save`}>Guardar</Button>
          <Button onClick={() => { setEditing(false); setValue(""); }}>Cancelar</Button>
        </div>
      ) : null}
      {shown && mode === "generate" && !save.isPending && !save.isError ? (
        <div className="vx-stack" style={{ marginTop: 6 }}>
          <Callout tone="warn" title="Copie agora">Configure este valor no outro sistema. Ele não aparece de novo: depois, só dá para gerar outro.</Callout>
          <div className="vx-copy">
            <input className="vx-input mono" readOnly value={shown} aria-label={label} data-testid="once-secret" onFocus={(e) => e.currentTarget.select()} />
            <Button icon={<Copy />} onClick={async () => { try { await navigator.clipboard.writeText(shown); toast.ok("Copiado"); } catch { toast.error("Não foi possível copiar", "Selecione e copie manualmente."); } }}>Copiar</Button>
            <Button variant="ghost" onClick={() => setShown(null)}>Já copiei</Button>
          </div>
        </div>
      ) : null}
      {hint ? <span className="vx-field__hint">{hint}</span> : null}
      <ConfirmDialog open={clearing} onOpenChange={setClearing} title={`Remover ${label}`} confirmLabel="Remover" destructive onConfirm={() => clear.mutateAsync(undefined)}>
        <p>O valor é apagado do cofre. Quem usa este segredo passa a usar a variável do servidor, se houver, ou falha até um novo valor ser definido.</p>
      </ConfirmDialog>
    </div>
  );
}

/** Botão "Testar conexão": pede o teste ao worker e mostra o resultado (ok, erro ou sem teste). */
export function ConnectionTestButton({ provider, params, label = "Testar conexão" }: { provider: TestProvider; params?: Record<string, unknown>; label?: string }) {
  const org = useOrg();
  const qc = useQueryClient();
  const isAdmin = useHasRole("admin");
  const issuer = typeof params?.issuer_id === "string" ? params.issuer_id : null;
  const connection = typeof params?.connection_id === "string" ? params.connection_id : null;
  // Transcrição: um teste por motor (engine_id); sem engine_id, o Whisper local.
  const engine = provider === "transcricao" ? (typeof params?.engine_id === "string" ? params.engine_id : "") : null;
  const key = ["integrations", "connection_test", org.id, provider, issuer ?? "", connection ?? "", engine ?? ""];
  const last = useQuery({
    queryKey: key,
    enabled: isAdmin,
    queryFn: async () => {
      let q = from("integrations", "connection_tests").select("*").eq("org_id", org.id).eq("provider", provider);
      if (issuer) q = q.eq("params->>issuer_id", issuer);
      if (connection) q = q.eq("params->>connection_id", connection);
      if (engine !== null) q = engine ? q.eq("params->>engine_id", engine) : q.is("params->>engine_id", null);
      const rows = await unwrap<ConnectionTest[]>(q.order("created_at", { ascending: false }).limit(1));
      return rows[0] ?? null;
    },
    refetchInterval: (q) => (q.state.data && ["pendente", "executando"].includes(q.state.data.status) ? 1500 : false),
  });
  const req = useAction(() => rpc("integrations", "request_connection_test", { p_org: org.id, p_provider: provider, p_params: params ?? {} }), {
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  if (!isAdmin) return null;
  const t = last.data;
  const running = req.isPending || (t && ["pendente", "executando"].includes(t.status));
  return (
    <div className="vx-row" style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid={connection ? `test-${provider}-${connection}` : engine ? `test-${provider}-${engine}` : `test-${provider}`}>
      <Button size="sm" icon={<PlugZap />} loading={Boolean(running)} onClick={() => req.mutate(undefined)}>{label}</Button>
      {t ? (
        <span className="vx-row" style={{ gap: 6, alignItems: "center" }} data-testid={`test-${provider}-result`}>
          <StatusFromMap map={TEST_STATUS} value={t.status} />
          {t.message ? <span className="text-2 vx-break" style={{ fontSize: "var(--fs-12)" }}>{t.message}</span> : null}
          {t.finished_at ? <span className="muted num" style={{ fontSize: "var(--fs-12)" }}>{formatRelative(t.finished_at)}</span> : null}
        </span>
      ) : null}
    </div>
  );
}
